import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { API_URL } from "./Constants";
import { Unlink } from "./Icons";

// Disconnecting drops the stored Volo credential, which takes auto-signup, the
// calendar feed and the reminders with it. Its own card at the foot of the
// page, behind a confirmation, so it can't be mistaken for "pause".
export const DisconnectCard = ({ refetch }: { refetch: () => void }) => {
  const [open, setOpen] = useState(false);
  const disconnect = useMutation({
    mutationFn: async () => {
      const res = await fetch(API_URL + "/stop", {
        method: "POST",
        credentials: "include",
      });
      if (res.status !== 200) {
        throw new Error(await res.text());
      }
      setOpen(false);
      refetch();
    },
  });

  return (
    <Card>
      <Box padding="2em">
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box sx={{ color: "error.main", display: "flex" }}>
            <Unlink width={22} height={22} />
          </Box>
          <Typography variant="h5">Disconnect</Typography>
        </Stack>

        {disconnect.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {disconnect.error.message || "Couldn't disconnect."}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Button color="error" onClick={() => setOpen(true)}>
            Unlink my Volo account
          </Button>
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unlink your Volo account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This stops auto-signup, your calendar feed and the daily reminder,
            and deletes the Volo login VoloPilot has stored. You'd need to
            connect again to use any of it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <LoadingButton
            loading={disconnect.isPending}
            variant="contained"
            color="error"
            onClick={() => disconnect.mutate()}
          >
            Unlink
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
